import { useMemo } from "react";

import moment from "moment-timezone";
import { useWatch } from "react-hook-form";

import { minAgendaMinutes } from "@helpers/_constants";

import { WEEK_DAYS } from "../ModalHourCreate/subComponents/WeeklyHoursSection";
import { combineDateTime, setCorrectDateToLunchHour } from "./helper";

// defaultFormNames = {
//     startDateTime: "startDateTime",
//     endTime: "endTime",
//     endDateTime: "endDateTime",
//     lunchStartDate: "lunchStartDate",
//     lunchEndDate: "lunchEndDate"
//   },
const getWeeklyFormName = (key, name) => `weeklyHours.${key}.${name}`;

function isLunchHourBetweenStartEndDate(
  { lunchDate, startDateTime, endDateTime },
  type = "daily"
) {
  if (!startDateTime || !endDateTime) {
    return type === "weekly";
  }

  lunchDate = setCorrectDateToLunchHour(startDateTime, endDateTime, lunchDate);

  return moment(lunchDate).isBetween(startDateTime, endDateTime, null, "()");
}

function isDurationGreaterThanOneMinute({
  newStartTime = null,
  newEndTime = null,
  type = "daily",
  startDateTime,
  endDateTime,
  lunchStartDate,
  lunchEndDate
}) {
  if (!newStartTime || !newEndTime) {
    return type === "weekly";
  }
  if (newStartTime && !moment(newStartTime).isSame(startDateTime, "minute")) {
    const mStartTime = moment(newStartTime);
    startDateTime = moment(startDateTime)
      .set({
        hour: mStartTime.hour(),
        minute: mStartTime.minute(),
        second: mStartTime.second()
      })
      .toDate();
  }
  if (newEndTime && !moment(newEndTime).isSame(endDateTime, "minute")) {
    const mEndTime = moment(newEndTime);
    endDateTime = moment(endDateTime)
      .set({
        hour: mEndTime.hour(),
        minute: mEndTime.minute(),
        second: mEndTime.second()
      })
      .toDate();
  }
  let timeDuration = moment(endDateTime).diff(startDateTime, "minutes");
  if (lunchStartDate && lunchEndDate) {
    lunchStartDate = setCorrectDateToLunchHour(
      startDateTime,
      endDateTime,
      lunchStartDate
    );
    lunchEndDate = setCorrectDateToLunchHour(
      startDateTime,
      endDateTime,
      lunchEndDate
    );
    const lunchDuration = moment(lunchEndDate).diff(lunchStartDate, "minutes");
    timeDuration -= lunchDuration;
  }

  return timeDuration >= minAgendaMinutes;
}

/**
 *
 * @param {Object} props
 * @param {import("react-hook-form").Control<any>} props.control
 * @param {import("react-hook-form").UseFormGetValues<any>} props.getValues
 * @returns
 */
export function useFormValidation({ control, getValues }) {
  const type = useWatch({ name: "type", control });
  const Groupes = useWatch({
    name: "Groupes",
    control,
    defaultValue: []
  });
  const Employees = useWatch({
    name: "Employees",
    control,
    defaultValue: []
  });
  const Emplacements = useWatch({
    name: "Emplacements",
    control,
    defaultValue: []
  });

  const output = {};

  output.Emplacements = useMemo(() => {
    return {
      validate: {
        required: (value) =>
          Groupes?.length > 0 || Employees?.length > 0 || value?.length > 0
      }
    };
  }, [Groupes, Employees]);

  output.Groupes = useMemo(() => {
    return {
      validate: {
        required: (value) =>
          Emplacements?.length > 0 || Employees?.length > 0 || value?.length > 0
      }
    };
  }, [Emplacements, Employees]);

  output.Employees = useMemo(() => {
    return {
      validate: {
        required: (value) =>
          Emplacements?.length > 0 || Groupes?.length > 0 || value?.length > 0
      }
    };
  }, [Emplacements, Groupes]);

  const dateRange = useWatch({
    name: "dateRange",
    control,
    defaultValue: {}
  });
  const startDateTime = useWatch({
    name: "startDateTime",
    control,
    defaultValue: null
  });
  const endDateTime = useWatch({
    name: "endDateTime",
    control,
    defaultValue: null
  });

  output.dateRange = useMemo(() => {
    return {
      required: true,
      validate: {
        isLessThanOneDay: () => {
          if (type === "weekly") return true;
          if (!startDateTime || !endDateTime) {
            return true;
          }

          return (
            moment.duration(moment(endDateTime).diff(startDateTime)).asDays() <
            1
          );
        },
        isRequired: () => Boolean(dateRange.startDate)
      }
    };
  }, [dateRange, startDateTime, endDateTime, type]);

  const startTime = useWatch({
    name: "startTime",
    control
  });
  const endTime = useWatch({
    name: "endTime",
    control
  });
  const lunchStartDate = useWatch({
    name: "lunchStartDate",
    control
  });
  const lunchEndDate = useWatch({
    name: "lunchEndDate",
    control
  });

  output.startTime = useMemo(() => {
    return {
      required: true,
      validate: {
        isAfterStartDate: () => moment(startDateTime).isBefore(endDateTime),
        isDurationGreaterThanOneMinute: (newStartTime) =>
          isDurationGreaterThanOneMinute({
            newStartTime,
            newEndTime: endTime,
            startDateTime,
            endDateTime,
            lunchStartDate,
            lunchEndDate
          })
      }
    };
  }, [endTime, startDateTime, endDateTime, lunchStartDate, lunchEndDate]);

  output.endTime = useMemo(() => {
    return {
      required: true,
      validate: {
        isAfterStartDate: () => moment(startDateTime).isBefore(endDateTime),
        isDurationGreaterThanOneMinute: (newEndTime) =>
          isDurationGreaterThanOneMinute({
            newEndTime,
            newStartTime: startTime,
            startDateTime,
            endDateTime,
            lunchStartDate,
            lunchEndDate
          })
      }
    };
  }, [startTime, startDateTime, endDateTime, lunchStartDate, lunchEndDate]);

  output.lunchStartDate = useMemo(() => {
    return {
      required: true,
      validate: {
        isBetweenStartAndEnd: (lunchDate) =>
          isLunchHourBetweenStartEndDate({
            lunchDate,
            startDateTime,
            endDateTime
          })
      }
    };
  }, [startDateTime, endDateTime]);

  output.lunchEndDate = useMemo(() => {
    return {
      required: true,
      validate: {
        isBetweenStartAndEnd: (lunchDate) =>
          isLunchHourBetweenStartEndDate({
            startDateTime,
            endDateTime,
            lunchDate
          }),
        isAfterLunchStart: (newLunchEndDate) => {
          let correctLunchEndDate = setCorrectDateToLunchHour(
              startDateTime,
              endDateTime,
              newLunchEndDate
            ),
            correctLunchStartDate = setCorrectDateToLunchHour(
              startDateTime,
              endDateTime,
              lunchStartDate
            );

          return moment(correctLunchEndDate).isAfter(correctLunchStartDate);
        }
      }
    };
  }, [startDateTime, endDateTime, lunchStartDate]);

  const weeklyHours = useWatch({
    name: "weeklyHours",
    control,
    defaultValue: {}
  });

  output.weeklyHours = useMemo(
    () =>
      WEEK_DAYS.reduce((acc, day) => {
        const formNames = [
          "startTime",
          "endTime",
          "lunchStartDate",
          "lunchEndDate"
        ].map((_inputName) => getWeeklyFormName(day.key, _inputName));

        const [
          startTime,
          endTime,
          lunchStartDate,
          lunchEndDate
        ] = getValues(formNames);

        // Weekly rules validate clock times independently of the selected period.
        // These dates are local validation references and are never saved to the form.
        const validationDate = moment().startOf("day").toDate();
        const startDateTime = startTime ? combineDateTime(validationDate, startTime) : null;
        const endDateTime = endTime ? combineDateTime(validationDate, endTime) : null;

        acc[day.key] = {
          startTime: {
            validate: {
              isRequired: (newStartTime) => !endTime || Boolean(newStartTime),
              isAfterStartDate: () =>
                !endTime || moment(startDateTime).isBefore(endDateTime),
              isDurationGreaterThanOneMinute: (newStartTime) =>
                isDurationGreaterThanOneMinute({
                  newStartTime,
                  newEndTime: endTime,
                  startDateTime,
                  endDateTime,
                  lunchStartDate,
                  lunchEndDate,
                  type: "weekly"
                })
            }
          },
          endTime: {
            validate: {
              isRequired: (newEndTime) => !startTime || Boolean(newEndTime),
              isAfterStartDate: () =>
                !startTime || moment(startDateTime).isBefore(endDateTime),
              isDurationGreaterThanOneMinute: (newEndTime) =>
                isDurationGreaterThanOneMinute({
                  newEndTime,
                  newStartTime: startTime,
                  startDateTime,
                  endDateTime,
                  lunchStartDate,
                  lunchEndDate,
                  type: "weekly"
                })
            }
          },
          lunchStartDate: {
            required: true,
            validate: {
              isBetweenStartAndEnd: (lunchDate) =>
                isLunchHourBetweenStartEndDate(
                  { lunchDate, startDateTime, endDateTime },
                  "weekly"
                )
            }
          },
          lunchEndDate: {
            required: true,
            validate: {
              isBetweenStartAndEnd: (lunchDate) =>
                isLunchHourBetweenStartEndDate(
                  { lunchDate, startDateTime, endDateTime },
                  "weekly"
                ),
              isAfterLunchStart: (newLunchEndDate) => {
                if (!lunchStartDate || !startDateTime || !endDateTime)
                  return true;
                let correctLunchEndDate = setCorrectDateToLunchHour(
                    startDateTime,
                    endDateTime,
                    newLunchEndDate
                  ),
                  correctLunchStartDate = setCorrectDateToLunchHour(
                    startDateTime,
                    endDateTime,
                    lunchStartDate
                  );
                return moment(correctLunchEndDate).isAfter(
                  correctLunchStartDate
                );
              }
            }
          }
        };
        return acc;
      }, {}),
    [weeklyHours, getValues]
  );

  return output;
}
